import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import ApiRequest from '../../../actions/ApiRequest';
import translate from '../../../i18n/translate';

const PermissionManager = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  
  // Data states
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Form states
  const [grantForm, setGrantForm] = useState({
    sellerid: '',
    permission_code: '',
    reason: '',
    expires_date: ''
  });
  
  const [revokeForm, setRevokeForm] = useState({
    sellerid: '',
    permission_code: '',
    reason: ''
  });
  
  const [assignRoleForm, setAssignRoleForm] = useState({
    sellerid: '',
    role_code: '',
    expires_date: ''
  });
  
  const [removeRoleForm, setRemoveRoleForm] = useState({
    sellerid: '',
    role_code: ''
  });
  
  const [viewUserId, setViewUserId] = useState('');
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Check if user is admin (100000)
  // Get seller_id from user data stored during login
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('companies') || localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.sellerid || parsed.seller_id || parsed.id;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
    return null;
  };
  
  const currentUserId = getUserData();
  const isAdmin = currentUserId === '100000' || currentUserId === 100000;

  useEffect(() => {
    if (isAdmin) {
      loadInitialData();
    }
  }, [isAdmin]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPermissions(),
        loadRoles(),
        loadAuditLog()
      ]);
    } catch (error) {
      showAlert('error', 'Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await ApiRequest.get('/permissions/list');
      if (response.data.status === 'success') {
        setPermissions(response.data.permissions || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await ApiRequest.get('/roles/list');
      if (response.data.status === 'success') {
        setRoles(response.data.roles || []);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadAuditLog = async () => {
    try {
      const response = await ApiRequest.get('/audit/permissions?limit=50');
      if (response.data.status === 'success') {
        setAuditLogs(response.data.audit_logs || []);
      }
    } catch (error) {
      console.error('Error loading audit log:', error);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const handleGrantPermission = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await ApiRequest.post('/permissions/grant', grantForm);
      if (response.data.status === 'success') {
        showAlert('success', `Permission granted successfully to user ${grantForm.sellerid}`);
        setGrantForm({ sellerid: '', permission_code: '', reason: '', expires_date: '' });
        loadAuditLog();
      } else {
        showAlert('error', response.data.reason || 'Failed to grant permission');
      }
    } catch (error) {
      showAlert('error', 'Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokePermission = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await ApiRequest.post('/permissions/revoke', revokeForm);
      if (response.data.status === 'success') {
        showAlert('success', `Permission revoked successfully from user ${revokeForm.sellerid}`);
        setRevokeForm({ sellerid: '', permission_code: '', reason: '' });
        loadAuditLog();
      } else {
        showAlert('error', response.data.reason || 'Failed to revoke permission');
      }
    } catch (error) {
      showAlert('error', 'Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await ApiRequest.post('/roles/assign', assignRoleForm);
      if (response.data.status === 'success') {
        showAlert('success', `Role assigned successfully to user ${assignRoleForm.sellerid}`);
        setAssignRoleForm({ sellerid: '', role_code: '', expires_date: '' });
        loadAuditLog();
      } else {
        showAlert('error', response.data.reason || 'Failed to assign role');
      }
    } catch (error) {
      showAlert('error', 'Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await ApiRequest.post('/roles/remove', removeRoleForm);
      if (response.data.status === 'success') {
        showAlert('success', `Role removed successfully from user ${removeRoleForm.sellerid}`);
        setRemoveRoleForm({ sellerid: '', role_code: '' });
        loadAuditLog();
      } else {
        showAlert('error', response.data.reason || 'Failed to remove role');
      }
    } catch (error) {
      showAlert('error', 'Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserPermissions = async () => {
    if (!viewUserId) {
      showAlert('error', 'Please enter a User ID');
      return;
    }
    
    setLoading(true);
    try {
      const response = await ApiRequest.get(`/permissions/user/${viewUserId}`);
      if (response.data.status === 'success') {
        setUserPermissions(response.data);
      } else {
        showAlert('error', response.data.reason || 'Failed to load user permissions');
      }
    } catch (error) {
      showAlert('error', 'Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Access denied for non-admin users
  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CancelIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to access this page.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only administrators (User ID: 100000) can manage permissions and roles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            🔐 RBAC Permission Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user permissions and roles
          </Typography>
        </Box>
      </Box>

      {/* Alert Messages */}
      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} variant="scrollable">
          <Tab label="Grant Permission" icon={<CheckCircleIcon />} iconPosition="start" />
          <Tab label="Revoke Permission" icon={<CancelIcon />} iconPosition="start" />
          <Tab label="Assign Role" icon={<GroupIcon />} iconPosition="start" />
          <Tab label="Remove Role" icon={<CancelIcon />} iconPosition="start" />
          <Tab label="View User" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Audit Log" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <Card>
        <CardContent>
          {/* Grant Permission Tab */}
          {currentTab === 0 && (
            <Box component="form" onSubmit={handleGrantPermission}>
              <Typography variant="h6" gutterBottom>Grant Permission to User</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User ID (Seller ID)"
                    value={grantForm.sellerid}
                    onChange={(e) => setGrantForm({ ...grantForm, sellerid: e.target.value })}
                    required
                    placeholder="e.g., 100001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Permission</InputLabel>
                    <Select
                      value={grantForm.permission_code}
                      onChange={(e) => setGrantForm({ ...grantForm, permission_code: e.target.value })}
                      label="Permission"
                    >
                      {permissions.map((perm) => (
                        <MenuItem key={perm.code} value={perm.code}>
                          {perm.name} {perm.dangerous && '⚠️'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason (Required)"
                    value={grantForm.reason}
                    onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                    required
                    placeholder="Why is this permission being granted?"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Expiration Date (Optional)"
                    value={grantForm.expires_date}
                    onChange={(e) => setGrantForm({ ...grantForm, expires_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  >
                    Grant Permission
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Revoke Permission Tab */}
          {currentTab === 1 && (
            <Box component="form" onSubmit={handleRevokePermission}>
              <Typography variant="h6" gutterBottom>Revoke Permission from User</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User ID (Seller ID)"
                    value={revokeForm.sellerid}
                    onChange={(e) => setRevokeForm({ ...revokeForm, sellerid: e.target.value })}
                    required
                    placeholder="e.g., 100001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Permission</InputLabel>
                    <Select
                      value={revokeForm.permission_code}
                      onChange={(e) => setRevokeForm({ ...revokeForm, permission_code: e.target.value })}
                      label="Permission"
                    >
                      {permissions.map((perm) => (
                        <MenuItem key={perm.code} value={perm.code}>
                          {perm.name} {perm.dangerous && '⚠️'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason (Required)"
                    value={revokeForm.reason}
                    onChange={(e) => setRevokeForm({ ...revokeForm, reason: e.target.value })}
                    required
                    placeholder="Why is this permission being revoked?"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
                  >
                    Revoke Permission
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Assign Role Tab */}
          {currentTab === 2 && (
            <Box component="form" onSubmit={handleAssignRole}>
              <Typography variant="h6" gutterBottom>Assign Role to User</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User ID (Seller ID)"
                    value={assignRoleForm.sellerid}
                    onChange={(e) => setAssignRoleForm({ ...assignRoleForm, sellerid: e.target.value })}
                    required
                    placeholder="e.g., 100001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={assignRoleForm.role_code}
                      onChange={(e) => setAssignRoleForm({ ...assignRoleForm, role_code: e.target.value })}
                      label="Role"
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.code} value={role.code}>
                          {role.name} ({role.permission_count} permissions)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Expiration Date (Optional)"
                    value={assignRoleForm.expires_date}
                    onChange={(e) => setAssignRoleForm({ ...assignRoleForm, expires_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <GroupIcon />}
                  >
                    Assign Role
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Remove Role Tab */}
          {currentTab === 3 && (
            <Box component="form" onSubmit={handleRemoveRole}>
              <Typography variant="h6" gutterBottom>Remove Role from User</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User ID (Seller ID)"
                    value={removeRoleForm.sellerid}
                    onChange={(e) => setRemoveRoleForm({ ...removeRoleForm, sellerid: e.target.value })}
                    required
                    placeholder="e.g., 100001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={removeRoleForm.role_code}
                      onChange={(e) => setRemoveRoleForm({ ...removeRoleForm, role_code: e.target.value })}
                      label="Role"
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.code} value={role.code}>
                          {role.name} ({role.permission_count} permissions)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
                  >
                    Remove Role
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* View User Permissions Tab */}
          {currentTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>View User Permissions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User ID (Seller ID)"
                    value={viewUserId}
                    onChange={(e) => setViewUserId(e.target.value)}
                    placeholder="e.g., 100001"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleViewUserPermissions}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <PersonIcon />}
                  >
                    Load Permissions
                  </Button>
                </Grid>
              </Grid>

              {userPermissions && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="h6" gutterBottom>Assigned Roles</Typography>
                      {userPermissions.roles && userPermissions.roles.length > 0 ? (
                        userPermissions.roles.map((role, index) => (
                          <Chip
                            key={index}
                            label={role.role_name}
                            color="primary"
                            sx={{ m: 0.5 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2">No roles assigned</Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <Typography variant="h6" gutterBottom>All Permissions ({userPermissions.permissions?.length || 0})</Typography>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {userPermissions.permissions && userPermissions.permissions.length > 0 ? (
                          userPermissions.permissions.map((perm, index) => (
                            <Chip
                              key={index}
                              label={perm}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2">No permissions</Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Audit Log Tab */}
          {currentTab === 5 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Permission Audit Log</Typography>
                <Button
                  variant="outlined"
                  onClick={loadAuditLog}
                  startIcon={<HistoryIcon />}
                >
                  Refresh
                </Button>
              </Box>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date/Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Target User</TableCell>
                      <TableCell>Permission/Role</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.length > 0 ? (
                      auditLogs.map((log, index) => (
                        <TableRow key={index}>
                          <TableCell>{new Date(log.performed_date).toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.action_type}
                              size="small"
                              color={log.action_type.includes('GRANT') || log.action_type.includes('ASSIGN') ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell>{log.target_sellerid}</TableCell>
                          <TableCell>{log.permission_code || log.role_code || '-'}</TableCell>
                          <TableCell>{log.performed_by}</TableCell>
                          <TableCell>{log.reason || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No audit logs found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PermissionManager;

